import { useState, useEffect, useRef } from 'react'
import { Table, Button, Modal, Form, Badge, Tab, Tabs, Spinner, Alert } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { recordingsApi, extensionsApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'

interface Recording {
  id: string
  name: string
  filename: string
  duration: number
  source: string
  category: string
  created_at: string
  tts_text?: string
  tts_language?: string
}

export default function Recordings() {
  const confirm = useConfirm()
  const [recordings, setRecordings] = useState<Recording[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingRecording, setEditingRecording] = useState<Recording | null>(null)

  // Play-on-extension state
  const [playOnExtRec, setPlayOnExtRec] = useState<Recording | null>(null)
  const [playOnExtList, setPlayOnExtList] = useState<{extension: string; name: string}[]>([])
  const [playOnExtSelected, setPlayOnExtSelected] = useState<string>('')
  const [playOnExtSubmitting, setPlayOnExtSubmitting] = useState(false)

  // Audio player state
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [playUrl, setPlayUrl] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    loadRecordings()
  }, [])

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  // Keep the output device awake. Bluetooth/USB headsets (SCO mode) need
  // 1–2s to switch from idle to active when audio first starts, which
  // clips the beginning of every recording. A silent looping audio at
  // volume 0 keeps the device engaged for the whole page lifetime.
  // Started on the first user interaction (autoplay policy).
  const keepAliveRef = useRef<HTMLAudioElement | null>(null)
  useEffect(() => {
    const startKeepAlive = () => {
      if (keepAliveRef.current) return
      // 1-second silent WAV (8kHz mono, 16-bit PCM) as data URI
      const sr = 8000, dur = 1, samples = sr * dur
      const dataLen = samples * 2
      const buf = new ArrayBuffer(44 + dataLen)
      const dv = new DataView(buf)
      const w = (o: number, s: string) => { for (let i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i)) }
      w(0, 'RIFF'); dv.setUint32(4, 36 + dataLen, true); w(8, 'WAVE')
      w(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true)
      dv.setUint32(24, sr, true); dv.setUint32(28, sr * 2, true); dv.setUint16(32, 2, true); dv.setUint16(34, 16, true)
      w(36, 'data'); dv.setUint32(40, dataLen, true)
      const blob = new Blob([buf], { type: 'audio/wav' })
      const audio = new Audio(URL.createObjectURL(blob))
      audio.loop = true
      audio.volume = 0
      audio.play().catch(() => { /* autoplay blocked, will retry on next interaction */ keepAliveRef.current = null })
      keepAliveRef.current = audio
      window.removeEventListener('click', startKeepAlive)
      window.removeEventListener('keydown', startKeepAlive)
    }
    window.addEventListener('click', startKeepAlive)
    window.addEventListener('keydown', startKeepAlive)
    return () => {
      window.removeEventListener('click', startKeepAlive)
      window.removeEventListener('keydown', startKeepAlive)
      if (keepAliveRef.current) {
        keepAliveRef.current.pause()
        keepAliveRef.current = null
      }
    }
  }, [])

  const loadRecordings = async () => {
    try {
      const response = await recordingsApi.list()
      setRecordings(response.data.items || [])
    } catch (error) {
      toast.error('Fehler beim Laden der Aufnahmen')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: 'Aufnahme löschen',
      message: 'Aufnahme wirklich löschen?',
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await recordingsApi.delete(id)
      toast.success('Aufnahme gelöscht')
      loadRecordings()
    } catch (error) {
      toast.error('Fehler beim Löschen')
    }
  }

  const handleEdit = (recording: Recording) => {
    setEditingRecording(recording)
    setShowEditModal(true)
  }

  const handleDownload = async (id: string, filename: string) => {
    try {
      const response = await recordingsApi.download(id)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      toast.error('Fehler beim Download')
    }
  }

  const openPlayOnExtension = async (rec: Recording) => {
    setPlayOnExtRec(rec)
    setPlayOnExtSelected('')
    try {
      const res = await extensionsApi.list()
      const items = (res.data.items || []) as {extension: string; name: string}[]
      setPlayOnExtList(items)
      if (items.length > 0) setPlayOnExtSelected(items[0].extension)
    } catch {
      toast.error('Nebenstellen konnten nicht geladen werden')
    }
  }

  const submitPlayOnExtension = async () => {
    if (!playOnExtRec || !playOnExtSelected) return
    setPlayOnExtSubmitting(true)
    try {
      const fd = new FormData()
      fd.append('extension', playOnExtSelected)
      await recordingsApi.playOnExtension(playOnExtRec.id, fd)
      toast.success(`Anruf an ${playOnExtSelected} wird aufgebaut`)
      setPlayOnExtRec(null)
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } }
      toast.error(err.response?.data?.detail || 'Fehler beim Initiieren des Anrufs')
    } finally {
      setPlayOnExtSubmitting(false)
    }
  }

  const handlePlay = async (id: string) => {
    // Toggle off if same recording is currently playing.
    if (playingId === id && audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
      if (playUrl && playUrl.startsWith('blob:')) URL.revokeObjectURL(playUrl)
      setPlayUrl(null)
      setPlayingId(null)
      setIsPlaying(false)
      return
    }

    // Stop any currently playing audio.
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    if (playUrl && playUrl.startsWith('blob:')) URL.revokeObjectURL(playUrl)

    try {
      const res = await fetch(recordingsApi.getPlayUrl(id), { cache: 'no-store' })
      if (!res.ok) throw new Error('load failed')
      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)

      const audio = new Audio(blobUrl)
      audioRef.current = audio
      audio.onended = () => {
        if (audioRef.current === audio) audioRef.current = null
        URL.revokeObjectURL(blobUrl)
        setPlayUrl(null)
        setPlayingId(null)
        setIsPlaying(false)
      }
      audio.onerror = () => {
        toast.error('Fehler beim Abspielen')
        if (audioRef.current === audio) audioRef.current = null
        URL.revokeObjectURL(blobUrl)
        setPlayUrl(null)
        setPlayingId(null)
        setIsPlaying(false)
      }

      setPlayUrl(blobUrl)
      setPlayingId(id)
      setIsPlaying(true)
      await audio.play()
    } catch {
      toast.error('Wiedergabe fehlgeschlagen')
      setPlayingId(null)
      setIsPlaying(false)
    }
  }

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause()
    }
    if (playUrl && playUrl.startsWith('blob:')) URL.revokeObjectURL(playUrl)
    setIsPlaying(false)
    setPlayingId(null)
    setPlayUrl(null)
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getSourceBadge = (source: string) => {
    switch (source) {
      case 'upload': return <Badge bg="primary">Upload</Badge>
      case 'microphone': return <Badge bg="success">Mikrofon</Badge>
      case 'extension': return <Badge bg="info">Nebenstelle</Badge>
      case 'tts': return <Badge bg="warning" text="dark">TTS</Badge>
      default: return <Badge bg="secondary">{source}</Badge>
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Aufnahmen</h4>
        <Button variant="primary" onClick={() => setShowModal(true)}>
          <i className="bi bi-plus-lg me-1"></i> Neue Aufnahme
        </Button>
      </div>

      {/* Audio Player Bar */}
      {playingId && (
        <div className="audio-player-bar mb-3 p-3 bg-dark text-white rounded">
          <div className="d-flex align-items-center gap-3">
            <Button
              variant="outline-light"
              size="sm"
              onClick={handleStop}
              className="rounded-circle"
              style={{ width: '36px', height: '36px' }}
            >
              <i className="bi bi-x-lg"></i>
            </Button>
            <div className="small text-light">
              <i className={`bi ${isPlaying ? 'bi-music-note-beamed' : 'bi-pause-fill'} me-2`}></i>
              {recordings.find(r => r.id === playingId)?.name || 'Wiedergabe'}
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th style={{ width: '50px' }}></th>
              <th>Name</th>
              <th>Kategorie</th>
              <th>Dauer</th>
              <th>Quelle</th>
              <th>Erstellt</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-4">Laden...</td></tr>
            ) : recordings.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-4 text-muted">Keine Aufnahmen vorhanden</td></tr>
            ) : (
              recordings.map(rec => (
                <tr key={rec.id} className={playingId === rec.id ? 'table-primary' : ''}>
                  <td>
                    <Button
                      variant={playingId === rec.id && isPlaying ? 'primary' : 'outline-primary'}
                      size="sm"
                      onClick={() => handlePlay(rec.id)}
                      className="rounded-circle"
                      style={{ width: '32px', height: '32px' }}
                    >
                      <i className={`bi ${playingId === rec.id && isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
                    </Button>
                  </td>
                  <td><strong>{rec.name}</strong></td>
                  <td><Badge bg="secondary">{rec.category}</Badge></td>
                  <td>{rec.duration ? formatDuration(rec.duration) : '-'}</td>
                  <td>{getSourceBadge(rec.source)}</td>
                  <td>{new Date(rec.created_at).toLocaleDateString('de-DE')}</td>
                  <td className="text-end">
                    <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => handleEdit(rec)} title="Bearbeiten">
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-success" size="sm" className="me-1" onClick={() => openPlayOnExtension(rec)} title="Auf Nebenstelle abspielen">
                      <i className="bi bi-telephone-outbound"></i>
                    </Button>
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleDownload(rec.id, rec.filename)} title="Herunterladen">
                      <i className="bi bi-download"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(rec.id)} title="Löschen">
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={!!playOnExtRec} onHide={() => !playOnExtSubmitting && setPlayOnExtRec(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Aufnahme auf Nebenstelle abspielen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Aufnahme: <strong>{playOnExtRec?.name}</strong>
            <br />
            <small className="text-muted">
              Die gewählte Nebenstelle klingelt — sobald abgehoben wird, wird die Aufnahme abgespielt.
            </small>
          </p>
          <Form.Group>
            <Form.Label>Nebenstelle</Form.Label>
            <Form.Select
              value={playOnExtSelected}
              onChange={(e) => setPlayOnExtSelected(e.target.value)}
              disabled={playOnExtSubmitting || playOnExtList.length === 0}
            >
              {playOnExtList.length === 0 ? (
                <option>Keine Nebenstellen verfügbar</option>
              ) : (
                playOnExtList.map(e => (
                  <option key={e.extension} value={e.extension}>
                    {e.extension} - {e.name}
                  </option>
                ))
              )}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPlayOnExtRec(null)} disabled={playOnExtSubmitting}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={submitPlayOnExtension}
            disabled={playOnExtSubmitting || !playOnExtSelected}
          >
            {playOnExtSubmitting ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-telephone-outbound me-1"></i>Anrufen & Abspielen</>}
          </Button>
        </Modal.Footer>
      </Modal>

      <RecordingModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSuccess={() => { setShowModal(false); loadRecordings(); }}
      />

      {editingRecording && (
        <EditRecordingModal
          show={showEditModal}
          recording={editingRecording}
          onHide={() => { setShowEditModal(false); setEditingRecording(null); }}
          onSuccess={() => { setShowEditModal(false); setEditingRecording(null); loadRecordings(); }}
        />
      )}
    </div>
  )
}

interface RecordingModalProps {
  show: boolean
  onHide: () => void
  onSuccess: () => void
}

interface Extension {
  id: string
  extension: string
  name: string
}

function RecordingModal({ show, onHide, onSuccess }: RecordingModalProps) {
  const [activeTab, setActiveTab] = useState('upload')
  const [name, setName] = useState('')
  const [category, setCategory] = useState('other')
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  // Microphone recording state
  const [isRecording, setIsRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [recordingTime, setRecordingTime] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Extension recording state
  const [extensions, setExtensions] = useState<Extension[]>([])
  const [selectedExtension, setSelectedExtension] = useState('')
  const [_extensionRecordingId, setExtensionRecordingId] = useState<string | null>(null)
  const [extensionRecordingStatus, setExtensionRecordingStatus] = useState<'idle' | 'calling' | 'recording' | 'completed'>('idle')
  const statusCheckRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // TTS state
  const [ttsText, setTtsText] = useState('')
  const [ttsLanguage, setTtsLanguage] = useState('piper_de')
  const [ttsGenerating, setTtsGenerating] = useState(false)
  const [ttsPreviewUrl, setTtsPreviewUrl] = useState<string | null>(null)
  const [ttsPreviewing, setTtsPreviewing] = useState(false)

  // TTS voices grouped by engine
  const ttsVoices = {
    piper: [
      { code: 'piper_de', name: 'Thorsten (männlich)', lang: 'Deutsch' },
      { code: 'piper_de_f', name: 'Eva (weiblich)', lang: 'Deutsch' },
      { code: 'piper_de_emo', name: 'Thorsten Emotional', lang: 'Deutsch' },
      { code: 'piper_en', name: 'Amy (female)', lang: 'English' },
      { code: 'piper_fr', name: 'Siwis', lang: 'Français' },
      { code: 'piper_it', name: 'Paola (weiblich)', lang: 'Italiano' },
      { code: 'piper_es', name: 'Davefx', lang: 'Español' },
    ],
    gtts: [
      { code: 'gtts_de', name: 'Google', lang: 'Deutsch' },
      { code: 'gtts_en', name: 'Google', lang: 'English' },
      { code: 'gtts_fr', name: 'Google', lang: 'Français' },
      { code: 'gtts_es', name: 'Google', lang: 'Español' },
      { code: 'gtts_it', name: 'Google', lang: 'Italiano' },
      { code: 'gtts_pt', name: 'Google', lang: 'Português' },
      { code: 'gtts_nl', name: 'Google', lang: 'Nederlands' },
      { code: 'gtts_pl', name: 'Google', lang: 'Polski' },
      { code: 'gtts_ru', name: 'Google', lang: 'Русский' },
      { code: 'gtts_tr', name: 'Google', lang: 'Türkçe' },
      { code: 'gtts_ar', name: 'Google', lang: 'العربية' },
      { code: 'gtts_ja', name: 'Google', lang: '日本語' },
      { code: 'gtts_ko', name: 'Google', lang: '한국어' },
      { code: 'gtts_zh', name: 'Google', lang: '中文' },
    ]
  }

  useEffect(() => {
    if (show) {
      extensionsApi.list().then(res => {
        setExtensions(res.data.items || [])
      }).catch(() => {})
    }
  }, [show])

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      mediaRecorder.ondataavailable = (e) => {
        chunksRef.current.push(e.data)
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/wav' })
        setAudioBlob(blob)
        stream.getTracks().forEach(track => track.stop())
      }

      mediaRecorder.start()
      setIsRecording(true)
      setRecordingTime(0)

      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1)
      }, 1000)
    } catch (error) {
      toast.error('Mikrofon-Zugriff verweigert')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }

  const handleUpload = async () => {
    if (!name) {
      toast.error('Bitte Namen eingeben')
      return
    }

    setUploading(true)
    const formData = new FormData()
    formData.append('name', name)
    formData.append('category', category)

    try {
      if (activeTab === 'upload' && file) {
        formData.append('file', file)
        await recordingsApi.upload(formData)
      } else if (activeTab === 'microphone' && audioBlob) {
        formData.append('file', audioBlob, 'recording.wav')
        await recordingsApi.uploadMicrophone(formData)
      } else {
        toast.error('Keine Datei ausgewählt')
        setUploading(false)
        return
      }

      toast.success('Aufnahme gespeichert')
      onSuccess()
      resetForm()
    } catch (error) {
      toast.error('Fehler beim Speichern')
    } finally {
      setUploading(false)
    }
  }

  const resetForm = () => {
    setName('')
    setCategory('other')
    setFile(null)
    setAudioBlob(null)
    setRecordingTime(0)
    setSelectedExtension('')
    setExtensionRecordingId(null)
    setExtensionRecordingStatus('idle')
    setTtsText('')
    setTtsLanguage('de')
    setTtsPreviewUrl(null)
    if (statusCheckRef.current) {
      clearInterval(statusCheckRef.current)
    }
  }

  const handleTtsPreview = async () => {
    if (!ttsText.trim()) {
      toast.error('Bitte Text eingeben')
      return
    }
    if (ttsText.length > 500) {
      toast.error('Text zu lang für Vorschau (max. 500 Zeichen)')
      return
    }

    setTtsPreviewing(true)
    try {
      const formData = new FormData()
      formData.append('text', ttsText)
      formData.append('language', ttsLanguage)

      const response = await recordingsApi.ttsPreview(formData)
      const blob = new Blob([response.data], { type: 'audio/wav' })
      const url = URL.createObjectURL(blob)

      if (ttsPreviewUrl) {
        URL.revokeObjectURL(ttsPreviewUrl)
      }
      setTtsPreviewUrl(url)
    } catch (error) {
      toast.error('Fehler bei TTS-Vorschau')
    } finally {
      setTtsPreviewing(false)
    }
  }

  const handleTtsGenerate = async () => {
    if (!name.trim()) {
      toast.error('Bitte Namen eingeben')
      return
    }
    if (!ttsText.trim()) {
      toast.error('Bitte Text eingeben')
      return
    }

    setTtsGenerating(true)
    try {
      await recordingsApi.ttsGenerate({
        text: ttsText,
        name: name,
        language: ttsLanguage,
        category: category
      })
      toast.success('TTS-Aufnahme erfolgreich erstellt')
      onSuccess()
      resetForm()
    } catch (error) {
      toast.error('Fehler bei TTS-Generierung')
    } finally {
      setTtsGenerating(false)
    }
  }

  const startExtensionRecording = async () => {
    if (!selectedExtension || !name) {
      toast.error('Bitte Name und Nebenstelle auswählen')
      return
    }

    setExtensionRecordingStatus('calling')
    const formData = new FormData()
    formData.append('extension', selectedExtension)
    formData.append('name', name)
    formData.append('category', category)

    try {
      const response = await recordingsApi.startExtensionRecording(formData)
      const recordingId = response.data.id
      setExtensionRecordingId(recordingId)
      setExtensionRecordingStatus('recording')
      toast.info('Anruf gestartet - bitte nehmen Sie ab und sprechen Sie nach dem Signalton')

      // Start polling for completion
      statusCheckRef.current = setInterval(async () => {
        try {
          const statusRes = await recordingsApi.checkRecordingStatus(recordingId)
          if (statusRes.data.status === 'completed') {
            setExtensionRecordingStatus('completed')
            if (statusCheckRef.current) {
              clearInterval(statusCheckRef.current)
            }
            toast.success('Aufnahme erfolgreich gespeichert')
            onSuccess()
            resetForm()
          }
        } catch {
          // Keep polling
        }
      }, 2000)

    } catch (error) {
      setExtensionRecordingStatus('idle')
      toast.error('Fehler beim Starten der Aufnahme')
    }
  }

  const cancelExtensionRecording = () => {
    if (statusCheckRef.current) {
      clearInterval(statusCheckRef.current)
    }
    setExtensionRecordingStatus('idle')
    setExtensionRecordingId(null)
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Neue Aufnahme erstellen</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Name *</Form.Label>
          <Form.Control
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Begrüßung Hauptmenü"
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Kategorie</Form.Label>
          <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="ivr">IVR</option>
            <option value="voicemail">Voicemail</option>
            <option value="moh">Wartemusik</option>
            <option value="announcement">Ansage</option>
            <option value="other">Sonstige</option>
          </Form.Select>
        </Form.Group>

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'upload')} className="mb-3">
          <Tab eventKey="upload" title={<><i className="bi bi-upload me-2"></i>Datei hochladen</>}>
            <div className="p-3">
              <Form.Control
                type="file"
                accept="audio/*"
                onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] || null)}
              />
              <Form.Text className="text-muted">
                Unterstützte Formate: WAV, MP3, GSM
              </Form.Text>
            </div>
          </Tab>

          <Tab eventKey="microphone" title={<><i className="bi bi-mic me-2"></i>Mikrofon</>}>
            <div className="audio-recorder p-4">
              <div className="mb-4">
                <div className="fs-1 mb-2">{formatTime(recordingTime)}</div>
                {audioBlob && !isRecording && (
                  <audio controls src={URL.createObjectURL(audioBlob)} className="w-100" />
                )}
              </div>

              <button
                type="button"
                className={`record-button ${isRecording ? 'recording' : ''}`}
                onClick={isRecording ? stopRecording : startRecording}
              >
                <i className={`bi ${isRecording ? 'bi-stop-fill' : 'bi-mic-fill'}`}></i>
              </button>

              <div className="mt-3 text-muted">
                {isRecording ? 'Aufnahme läuft... Klicken zum Stoppen' : 'Klicken zum Starten der Aufnahme'}
              </div>
            </div>
          </Tab>

          <Tab eventKey="extension" title={<><i className="bi bi-telephone me-2"></i>Per Nebenstelle</>}>
            <div className="p-3">
              {extensionRecordingStatus === 'idle' ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Nebenstelle anrufen</Form.Label>
                    <Form.Select
                      value={selectedExtension}
                      onChange={(e) => setSelectedExtension(e.target.value)}
                    >
                      <option value="">-- Nebenstelle wählen --</option>
                      {extensions.map(ext => (
                        <option key={ext.id} value={ext.extension}>
                          {ext.extension} - {ext.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <p className="text-muted">
                    Die Nebenstelle wird angerufen und die Aufnahme nach dem Signalton gestartet.
                    <strong>#</strong> drücken oder auflegen um die Aufnahme zu beenden.
                  </p>
                  <Button
                    variant="primary"
                    onClick={startExtensionRecording}
                    disabled={!selectedExtension || !name}
                  >
                    <i className="bi bi-telephone-outbound me-2"></i>
                    Anruf starten
                  </Button>
                </>
              ) : (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <h5>
                    {extensionRecordingStatus === 'calling' && 'Rufe Nebenstelle an...'}
                    {extensionRecordingStatus === 'recording' && 'Aufnahme läuft...'}
                    {extensionRecordingStatus === 'completed' && 'Aufnahme abgeschlossen!'}
                  </h5>
                  <Alert variant="info" className="mt-3">
                    <i className="bi bi-info-circle me-2"></i>
                    Nehmen Sie das Telefon ab und sprechen Sie nach dem Signalton.<br />
                    <strong>#</strong> drücken oder auflegen um die Aufnahme zu beenden.
                  </Alert>
                  <Button
                    variant="outline-secondary"
                    onClick={cancelExtensionRecording}
                    className="mt-2"
                  >
                    Abbrechen
                  </Button>
                </div>
              )}
            </div>
          </Tab>

          <Tab eventKey="tts" title={<><i className="bi bi-chat-dots me-2"></i>Text-to-Speech</>}>
            <div className="p-3">
              <Form.Group className="mb-3">
                <Form.Label>Stimme / Voice</Form.Label>
                <Form.Select
                  value={ttsLanguage}
                  onChange={(e) => setTtsLanguage(e.target.value)}
                >
                  <optgroup label="🎙️ Piper TTS (Lokal, hochwertig)">
                    {ttsVoices.piper.map(v => (
                      <option key={v.code} value={v.code}>{v.lang} - {v.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="🌐 Google TTS (Online)">
                    {ttsVoices.gtts.map(v => (
                      <option key={v.code} value={v.code}>{v.lang} - {v.name}</option>
                    ))}
                  </optgroup>
                </Form.Select>
                <Form.Text className="text-muted">
                  Piper: Offline, schnell, natürliche Stimmen | Google: Online, viele Sprachen
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Text</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={ttsText}
                  onChange={(e) => setTtsText(e.target.value)}
                  placeholder="Geben Sie den Text ein, der vorgelesen werden soll..."
                />
                <Form.Text className="text-muted">
                  {ttsText.length} Zeichen
                </Form.Text>
              </Form.Group>

              {ttsPreviewUrl && (
                <div className="mb-3">
                  <Form.Label>Vorschau</Form.Label>
                  <audio controls src={ttsPreviewUrl} className="w-100" />
                </div>
              )}

              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={handleTtsPreview}
                  disabled={ttsPreviewing || !ttsText.trim()}
                >
                  {ttsPreviewing ? (
                    <><Spinner size="sm" className="me-2" />Generiere...</>
                  ) : (
                    <><i className="bi bi-play-circle me-2"></i>Vorschau</>
                  )}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleTtsGenerate}
                  disabled={ttsGenerating || !ttsText.trim() || !name.trim()}
                >
                  {ttsGenerating ? (
                    <><Spinner size="sm" className="me-2" />Generiere...</>
                  ) : (
                    <><i className="bi bi-save me-2"></i>Aufnahme erstellen</>
                  )}
                </Button>
              </div>

              <Alert variant="info" className="mt-3">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Hinweis:</strong> Die TTS-Generierung verwendet Piper TTS mit natürlicher Sprachausgabe.
              </Alert>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => { onHide(); resetForm(); }}>Abbrechen</Button>
        <Button variant="primary" onClick={handleUpload} disabled={uploading || !name}>
          {uploading ? 'Speichern...' : 'Speichern'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}


interface EditRecordingModalProps {
  show: boolean
  recording: Recording
  onHide: () => void
  onSuccess: () => void
}

function EditRecordingModal({ show, recording, onHide, onSuccess }: EditRecordingModalProps) {
  const [name, setName] = useState(recording.name)
  const [category, setCategory] = useState(recording.category)
  const [saving, setSaving] = useState(false)

  // Re-record via extension state
  const [extensions, setExtensions] = useState<{id: string, extension: string, name: string}[]>([])
  const [selectedExtension, setSelectedExtension] = useState('')
  const [reRecordStatus, setReRecordStatus] = useState<'idle' | 'calling' | 'recording' | 'completed'>('idle')
  const reRecordPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // TTS editing state
  const [ttsText, setTtsText] = useState(recording.tts_text || '')
  const [ttsLanguage, setTtsLanguage] = useState(recording.tts_language || 'piper_de')
  const [regenerating, setRegenerating] = useState(false)

  // TTS voices
  const ttsVoices = {
    piper: [
      { code: 'piper_de', name: 'Thorsten (männlich)', lang: 'Deutsch' },
      { code: 'piper_de_f', name: 'Eva (weiblich)', lang: 'Deutsch' },
      { code: 'piper_de_emo', name: 'Thorsten Emotional', lang: 'Deutsch' },
      { code: 'piper_en', name: 'Amy (female)', lang: 'English' },
      { code: 'piper_fr', name: 'Siwis', lang: 'Français' },
      { code: 'piper_it', name: 'Paola (weiblich)', lang: 'Italiano' },
      { code: 'piper_es', name: 'Davefx', lang: 'Español' },
    ],
    gtts: [
      { code: 'gtts_de', name: 'Google', lang: 'Deutsch' },
      { code: 'gtts_en', name: 'Google', lang: 'English' },
      { code: 'gtts_fr', name: 'Google', lang: 'Français' },
      { code: 'gtts_es', name: 'Google', lang: 'Español' },
      { code: 'gtts_it', name: 'Google', lang: 'Italiano' },
    ]
  }

  useEffect(() => {
    if (show) {
      setName(recording.name)
      setCategory(recording.category)
      setTtsText(recording.tts_text || '')
      setTtsLanguage(recording.tts_language || 'piper_de')
      setReRecordStatus('idle')
      setSelectedExtension('')
      extensionsApi.list().then(res => {
        setExtensions(res.data.items || [])
      }).catch(() => {})
    }
    return () => {
      if (reRecordPollRef.current) clearInterval(reRecordPollRef.current)
    }
  }, [show, recording])

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Bitte Namen eingeben')
      return
    }

    setSaving(true)
    try {
      await recordingsApi.update(recording.id, { name, category })
      toast.success('Aufnahme aktualisiert')
      onSuccess()
    } catch (error) {
      toast.error('Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }

  const handleRegenerate = async () => {
    if (!ttsText.trim()) {
      toast.error('Bitte Text eingeben')
      return
    }

    setRegenerating(true)
    try {
      await recordingsApi.regenerateTts(recording.id, {
        text: ttsText,
        language: ttsLanguage
      })
      toast.success('TTS-Aufnahme neu generiert')
      onSuccess()
    } catch (error) {
      toast.error('Fehler bei TTS-Regenerierung')
    } finally {
      setRegenerating(false)
    }
  }

  const handleReRecord = async () => {
    if (!selectedExtension) {
      toast.error('Bitte Nebenstelle auswählen')
      return
    }

    setReRecordStatus('calling')
    const formData = new FormData()
    formData.append('extension', selectedExtension)

    try {
      await recordingsApi.reRecordViaExtension(recording.id, formData)
      setReRecordStatus('recording')
      toast.info('Anruf gestartet - bitte nehmen Sie ab und sprechen Sie nach dem Signalton')

      reRecordPollRef.current = setInterval(async () => {
        try {
          const statusRes = await recordingsApi.checkRecordingStatus(recording.id)
          if (statusRes.data.status === 'completed') {
            setReRecordStatus('completed')
            if (reRecordPollRef.current) clearInterval(reRecordPollRef.current)
            toast.success('Aufnahme erfolgreich neu besprochen')
            onSuccess()
          }
        } catch {
          // Keep polling
        }
      }, 2000)
    } catch {
      setReRecordStatus('idle')
      toast.error('Fehler beim Starten der Aufnahme')
    }
  }

  const isTtsRecording = recording.source === 'tts'

  return (
    <Modal show={show} onHide={onHide} size={isTtsRecording ? 'lg' : undefined}>
      <Modal.Header closeButton>
        <Modal.Title>Aufnahme bearbeiten</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Kategorie</Form.Label>
          <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="ivr">IVR</option>
            <option value="voicemail">Voicemail</option>
            <option value="moh">Wartemusik</option>
            <option value="announcement">Ansage</option>
            <option value="system">System</option>
            <option value="other">Sonstige</option>
          </Form.Select>
        </Form.Group>

        {!isTtsRecording && (
          <>
            <hr />
            <h6 className="mb-3">
              <i className="bi bi-telephone me-2"></i>
              Per Nebenstelle neu besprechen
            </h6>

            {reRecordStatus === 'idle' ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Nebenstelle anrufen</Form.Label>
                  <Form.Select
                    value={selectedExtension}
                    onChange={(e) => setSelectedExtension(e.target.value)}
                  >
                    <option value="">-- Nebenstelle wählen --</option>
                    {extensions.map(ext => (
                      <option key={ext.id} value={ext.extension}>
                        {ext.extension} - {ext.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Button
                  variant="warning"
                  onClick={handleReRecord}
                  disabled={!selectedExtension}
                >
                  <i className="bi bi-arrow-repeat me-2"></i>
                  Neu besprechen
                </Button>
                <Alert variant="info" className="mt-3 mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Die Aufnahme behält ihre ID. Alle Verweise (IVR, Ansagen, etc.) bleiben erhalten.
                </Alert>
              </>
            ) : (
              <div className="text-center py-3">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <h6>
                  {reRecordStatus === 'calling' && 'Rufe Nebenstelle an...'}
                  {reRecordStatus === 'recording' && 'Aufnahme läuft... # drücken oder auflegen um zu beenden.'}
                  {reRecordStatus === 'completed' && 'Aufnahme abgeschlossen!'}
                </h6>
              </div>
            )}
          </>
        )}

        {isTtsRecording && (
          <>
            <hr />
            <h6 className="mb-3">
              <i className="bi bi-chat-dots me-2"></i>
              Text-to-Speech bearbeiten
            </h6>

            <Form.Group className="mb-3">
              <Form.Label>Stimme / Voice</Form.Label>
              <Form.Select
                value={ttsLanguage}
                onChange={(e) => setTtsLanguage(e.target.value)}
              >
                <optgroup label="🎙️ Piper TTS (Lokal)">
                  {ttsVoices.piper.map(v => (
                    <option key={v.code} value={v.code}>{v.lang} - {v.name}</option>
                  ))}
                </optgroup>
                <optgroup label="🌐 Google TTS (Online)">
                  {ttsVoices.gtts.map(v => (
                    <option key={v.code} value={v.code}>{v.lang} - {v.name}</option>
                  ))}
                </optgroup>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Text</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={ttsText}
                onChange={(e) => setTtsText(e.target.value)}
                placeholder="Text zum Vorlesen eingeben..."
              />
              <Form.Text className="text-muted">
                {ttsText.length} Zeichen
              </Form.Text>
            </Form.Group>

            <Button
              variant="warning"
              onClick={handleRegenerate}
              disabled={regenerating || !ttsText.trim()}
              className="mb-3"
            >
              {regenerating ? (
                <><Spinner size="sm" className="me-2" />Generiere neu...</>
              ) : (
                <><i className="bi bi-arrow-repeat me-2"></i>Audio neu generieren</>
              )}
            </Button>

            <Alert variant="info" className="mb-0">
              <i className="bi bi-info-circle me-2"></i>
              Die Aufnahme behält ihre ID. Alle Verweise (IVR, Ansagen, etc.) bleiben erhalten.
            </Alert>
          </>
        )}

        <div className="text-muted small mt-3">
          <strong>Quelle:</strong> {recording.source}<br />
          <strong>Datei:</strong> {recording.filename}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Abbrechen</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Speichern...' : 'Speichern'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
